import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../state'

import * as generalTab from './general-tab'
import * as usersTab from './users-tab'

/** Admin detail page for the selected scenario and its management tabs. */
@ngCore.Component({
  imports: [
    ds.tabs.floatingHorizontal.Component,
    ds.tabs.tab.Directive,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    generalTab.Component,
    usersTab.Component,
  ],
  selector: 'atn-admin-scenario-page',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.admin.tenants.Collection} */
  public readonly tenantsEc = ngCore.inject(state.admin.tenants.Collection)

  /** @see {@link state.admin.zones.Collection} */
  public readonly zonesEc = ngCore.inject(state.admin.zones.Collection)

  /** @see {@link state.admin.apps.Collection} */
  public readonly appsEc = ngCore.inject(state.admin.apps.Collection)

  /** @see {@link state.admin.scenarios.Collection} */
  public readonly scenariosEc = ngCore.inject(state.admin.scenarios.Collection)

  /** @see {@link state.admin.contexts.Feature} */
  private readonly _contextsFeature = ngCore.inject(
    state.admin.contexts.Feature,
  )

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** Currently selected scenario entity for display-only metadata. */
  private readonly _scenarioSg =
    this.scenariosEc.plugins.currentEntityPlugin.feature.selectors.getEntity()
      .asSignal

  /** Name of selected scenario for the page header and breadcrumbs. */
  public readonly scenarioNameSg = ngCore.computed(
    (scenario = this._scenarioSg()) =>
      scenario?.name ?? scenario?.alias ?? 'Scenario',
  )

  /**
   * Breadcrumbs displayed in page header
   */
  public readonly breadcrumbsSg = ngCore.computed(
    (
      app = this.appsEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal(),
      zone = this.zonesEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal(),
      tenant = this.tenantsEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal(),
    ) => {
      const breadcrumbs: ds.layout.page.child.header.models.Breadcrumb[] = []

      breadcrumbs.push({
        name: 'Tenants',
        url: this._contextsFeature.selectors.getUrlForTenants().asSignal(),
      })

      if (tenant) {
        const tenantUrl = this._contextsFeature.selectors
          .getUrlForTenant(tenant)
          .asSignal()
        if (tenantUrl) {
          breadcrumbs.push({
            name: tenant.name ?? tenant.alias ?? '',
            url: tenantUrl,
          })
        }
      }

      if (zone) {
        const zoneUrl = this._contextsFeature.selectors
          .getUrlForZone(zone)
          .asSignal()
        if (zoneUrl) {
          breadcrumbs.push({
            name: zone.name ?? zone.alias ?? '',
            url: zoneUrl,
          })
        }
      }

      if (app) {
        const appUrl = this._contextsFeature.selectors
          .getUrlForApp(app)
          .asSignal()
        if (appUrl) {
          breadcrumbs.push({
            name: app.name ?? app.alias ?? '',
            url: appUrl,
          })
        }
      }

      breadcrumbs.push({
        name: this.scenarioNameSg() ?? '',
      })

      return breadcrumbs
    },
  )

  /** Whether the Users tab is available for the current admin user */
  public readonly canManageUsersSg = ngCore.computed(
    (
      context = this._contextsFeature.selectors.activeEntityContext.asSignal(),
      currentUser = this._meFeature.selectors.currentUser.asSignal(),
    ) =>
      state.admin.userAssignment.models.User.canManageUsersForContext(
        currentUser,
        context,
      ),
  )
}
